import { useState } from 'react'
import { api, type Redemption } from '../api'
import { BrandLogo, BrandShell, Wordmark } from '../components/Brand'

/**
 * Redeeming an invitation — TECHNICAL_DESIGN.md §10.1.
 *
 * **Without this screen a fresh appliance cannot be signed into at all.**
 * `bootstrap-admin` prints an invitation and says to redeem it in the browser; this
 * is where it is redeemed. A password is all it sets: a second factor is the firm's
 * identity provider's, not GUARD's (IMPLEMENTATION.md decision 88).
 */
export function Redeem({ onDone }: { onDone: () => void }) {
  const [token, setToken] = useState('')
  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [result, setResult] = useState<Redemption | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  async function submit(event: React.FormEvent) {
    event.preventDefault()
    if (password !== confirm) {
      setError('The two passwords do not match.')
      return
    }
    setBusy(true)
    setError(null)
    try {
      setResult(await api.redeem(token.trim(), password))
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setBusy(false)
    }
  }

  if (result) {
    return (
      <BrandShell>
        <section className="signin">
        <h1 className="product-title">
          <Wordmark size="title" />
        </h1>
        <p className="by-line">
          <span className="by">by</span>
          <BrandLogo className="by-logo" />
        </p>
        <h2>Account ready</h2>
        <p>
          Your password is set for <strong>{result.email}</strong>.
        </p>

        <p className="note">You can sign in now.</p>

        <button onClick={onDone}>Go to sign in</button>
        </section>
      </BrandShell>
    )
  }

  return (
    <BrandShell>
      <form className="signin" onSubmit={submit}>
        <h1 className="product-title">
        <Wordmark size="title" />
      </h1>
      <p className="by-line">
        <span className="by">by</span>
        <BrandLogo className="by-logo" />
      </p>
      <h2>Redeem your invitation</h2>
      <p className="note">
        Your administrator gave you a single-use invitation. It expires, and it
        can only be used once.
      </p>
      <label>
        Invitation
        <input value={token} onChange={(e) => setToken(e.target.value)} required />
      </label>
      <label>
        Choose a password
        <input
          type="password"
          value={password}
          autoComplete="new-password"
          onChange={(e) => setPassword(e.target.value)}
          required
        />
      </label>
      <label>
        Confirm password
        <input
          type="password"
          value={confirm}
          autoComplete="new-password"
          onChange={(e) => setConfirm(e.target.value)}
          required
        />
      </label>
      {error && (
        <p className="error" role="alert">
          {error}
        </p>
      )}
      <button type="submit" disabled={busy}>
        {busy ? 'Setting up…' : 'Set password'}
      </button>
      <p className="federated">
        <a
          href="#sign-in"
          onClick={(e) => {
            e.preventDefault()
            onDone()
          }}
        >
          Back to sign in
        </a>
      </p>
      </form>
    </BrandShell>
  )
}
